import { useState } from "react";
import { Box, Group, Burger, Drawer } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { SidebarContent } from "../components/layout/SidebarContent";
import { HomePage } from "../components/home/HomePage";
import { Logo } from "../../../../layouts/Logo";

/**
 * Standalone Home demo — reproduces the full mockup (foldable navbar + content)
 * WITHOUT Mantine AppShell, using only Group / Stack / Box.
 *
 * Designed to drop into an existing layout: it does NOT force its own height,
 * so it uses the parent's scroll (no double scrollbar). The sidebar is sticky.
 *
 *  - Desktop : inline sticky sidebar, foldable 280px <-> 84px (chevron).
 *  - Mobile  : sidebar hidden, opened as a Drawer overlay via the burger.
 */
export function HomeDemo() {
  const isMobile = useMediaQuery("(max-width: 48em)", false);
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const sidebarWidth = collapsed ? 84 : 250;
  const border = "1px solid var(--mantine-color-gray-2)";

  return (
    <Box bg={"#fcfcfd"}>
      {/* Mobile top bar (sticky) */}
      {isMobile && (
        <Group
          h={56}
          px="md"
          gap="sm"
          style={{
            borderBottom: border,
            position: "sticky",
            top: 0,
            zIndex: 50,
            background: "var(--mantine-color-body)",
          }}
        >
          <Burger
            opened={drawerOpened}
            onClick={openDrawer}
            size="sm"
            aria-label="Menu"
          />
          <Logo w={250} />
        </Group>
      )}

      <Group align="flex-start" wrap="nowrap" gap={0}>
        {/* Desktop inline sidebar — sticky, floats within the parent scroll */}
        {!isMobile && (
          <Box
            style={{
              width: sidebarWidth,
              flexShrink: 0,
              borderRight: border,
              transition: "width 160ms ease",
              position: "sticky",
              top: 0,
              alignSelf: "flex-start",
              height: "100vh",
              display: "flex",
            }}
          >
            <SidebarContent
              collapsed={collapsed}
              onToggleCollapse={() => setCollapsed((c) => !c)}
              showCollapseButton
            />
          </Box>
        )}

        {/* Content */}
        <Box
          p={{ base: "md", sm: "lg", lg: "xl" }}
          style={{ flex: 1, minWidth: 0 }}
        >
          <HomePage />
        </Box>
      </Group>

      {/* Mobile navbar overlay */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size={280}
        padding={0}
        withCloseButton={false}
        styles={{ body: { height: "100%" } }}
      >
        <SidebarContent
          collapsed={false}
          showCollapseButton={false}
          onNavigate={closeDrawer}
          onCloseMobile={closeDrawer}
        />
      </Drawer>
    </Box>
  );
}

export default HomeDemo;
